import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { API } from '../../App';
import { WEEKDAYS, dayLabel } from '../employee/RestDaySelect';

const FIELDS = [
  { key: 'company_name', label: 'Company Name', placeholder: 'My HR Orbit Sdn Bhd' },
  { key: 'company_address', label: 'Company Address', placeholder: 'No. 1, Jalan ..., 50000 Kuala Lumpur' },
  { key: 'ssm_number', label: 'SSM / MyCoID Registration No.', placeholder: '202001012345 (1234567-X)' },
  { key: 'employer_tin', label: 'LHDN Employer No. (E number)', placeholder: '1234567890', hint: 'Digits only — used in the e-PCB CP39 file and Form E' },
  { key: 'employer_branch_number', label: 'LHDN Branch Employer No. (optional)', placeholder: 'Leave blank if none' },
  { key: 'epf_employer_number', label: 'KWSP Employer Reference No.', placeholder: '12345678', hint: 'Used in the KWSP i-Akaun Borang A file' },
  { key: 'socso_employer_code', label: 'PERKESO Employer Code', placeholder: 'A1234567890X', hint: 'Used in the PERKESO ASSIST file and Borang 8A' },
  { key: 'statutory_contact_name', label: 'Contact Person (statutory submissions)', placeholder: 'HR Manager name' },
  { key: 'statutory_contact_phone', label: 'Contact Phone', placeholder: '0312345678' },
  { key: 'contact_email', label: 'Contact Email', placeholder: 'hr@company.com', type: 'email' },
];

export const CompanySettingsDialog = ({ open, onClose, canEdit }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [readiness, setReadiness] = useState({});

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/company-settings`)
      .then((res) => { const { readiness: r, ...rest } = res.data; setForm(rest); setReadiness(r || {}); })
      .catch(() => toast.error('Failed to load company settings'));
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(FIELDS.map((f) => [f.key, form[f.key] || '']));
      payload.default_rest_day = form.default_rest_day || 'sunday';
      payload.working_days = form.working_days || [];
      if (!payload.working_days.length) { toast.error('Select at least one working day'); setSaving(false); return; }
      if (payload.working_days.includes(payload.default_rest_day)) { toast.error('The rest day cannot also be a working day'); setSaving(false); return; }
      const res = await axios.put(`${API}/company-settings`, payload);
      setReadiness(res.data.readiness || {});
      toast.success('Company settings saved');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save company settings');
    } finally {
      setSaving(false);
    }
  };

  const missing = [...new Set(Object.values(readiness).flat())];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="company-settings-dialog">
        <DialogHeader>
          <DialogTitle>Company Settings &amp; Statutory Registration</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500">These identifiers are printed on EA / Form E / CP forms and embedded in the KWSP, PERKESO and LHDN upload files.</p>
        {missing.length > 0 && (
          <div className="text-xs rounded-md border border-amber-300 bg-amber-50 text-amber-900 p-2.5" data-testid="company-settings-missing">
            Missing for statutory exports: {missing.join(', ')}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          {FIELDS.map((f) => (
            <div key={f.key} className={f.key === 'company_address' ? 'md:col-span-2' : ''}>
              <Label htmlFor={`cs-${f.key}`}>{f.label}</Label>
              <Input id={`cs-${f.key}`} type={f.type || 'text'} placeholder={f.placeholder} className="mt-1" disabled={!canEdit}
                value={form[f.key] || ''} onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                data-testid={`company-settings-${f.key}`} />
              {f.hint && <p className="text-[11px] text-slate-500 mt-1">{f.hint}</p>}
            </div>
          ))}
        </div>
        <div className="mt-4 rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-3" data-testid="company-work-week">
          <p className="text-sm font-semibold">Work week &amp; rest day (Employment Act 1955)</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="cs-default_rest_day">Default designated rest day</Label>
              <select id="cs-default_rest_day" disabled={!canEdit} value={form.default_rest_day || 'sunday'}
                onChange={(e) => setForm((prev) => ({ ...prev, default_rest_day: e.target.value }))}
                className="mt-1 w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 px-3 bg-white dark:bg-slate-900 dark:text-slate-100"
                data-testid="company-settings-default_rest_day">
                {WEEKDAYS.map((d) => <option key={d} value={d}>{dayLabel(d)}</option>)}
              </select>
              <p className="text-[11px] text-slate-500 mt-1">Used when an employee has no rest day of their own. Only this day gets rest-day OT rates — Sunday is not automatically a rest day.</p>
            </div>
            <div>
              <Label>Working days</Label>
              <div className="mt-1 flex flex-wrap gap-2">
                {WEEKDAYS.map((d) => {
                  const on = (form.working_days || []).includes(d);
                  return (
                    <button key={d} type="button" disabled={!canEdit} data-testid={`company-settings-working-day-${d}`}
                      onClick={() => setForm((prev) => ({ ...prev, working_days: on ? (prev.working_days || []).filter((x) => x !== d) : [...(prev.working_days || []), d] }))}
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${on ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600'} disabled:opacity-60`}>
                      {dayLabel(d).slice(0, 3)}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-500 mt-1">A non-working day that is not the rest day is an "Off Day" — overtime there is paid at 1.5 × HRP.</p>
            </div>
          </div>
        </div>
        <div className="flex justify-end space-x-2 pt-4">
          <Button variant="outline" onClick={onClose} data-testid="company-settings-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || !canEdit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="company-settings-save">
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
